import React, { useState, useEffect } from 'react';
import { userAPI, cityAPI } from '../services/api';
import { User, City } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Collapse,
  Chip,
  Checkbox,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Edit, Delete, Refresh, FilterList, Clear, Add, Email, Phone } from '@mui/icons-material';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterForm, setFilterForm] = useState({
    username: '',
    email: '',
    cityId: '',
    role: '',
    isActive: ''
  });
  
  const [form, setForm] = useState<{
    username: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    cityId: string;
    role: string;
    isActive: boolean;
  }>({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    cityId: '',
    role: 'USER',
    isActive: true
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  const roles = [
    { value: 'USER', label: 'User' },
    { value: 'ADMIN', label: 'Admin' },
    { value: 'MODERATOR', label: 'Moderator' }
  ];

  useEffect(() => {
    fetchUsers();
    fetchCities();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await userAPI.getAllUsers();
      setUsers(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const res = await cityAPI.getAll();
      setCities(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Failed to load cities:', error);
    }
  };

  const applyFilters = async () => {
    setFilterLoading(true);
    setError(null);
    try {
      // For now, we'll filter client-side since the API doesn't support filtering
      // In a real implementation, you'd pass filter params to the API
      const res = await userAPI.getAllUsers();
      let filteredUsers = res.data;
      
      if (filterForm.username) {
        filteredUsers = filteredUsers.filter(user => 
          user.username.toLowerCase().includes(filterForm.username.toLowerCase())
        );
      }
      if (filterForm.email) {
        filteredUsers = filteredUsers.filter(user => 
          user.email.toLowerCase().includes(filterForm.email.toLowerCase())
        );
      }
      if (filterForm.cityId) {
        filteredUsers = filteredUsers.filter(user => 
          user.cityId === filterForm.cityId
        );
      }
      if (filterForm.role) {
        filteredUsers = filteredUsers.filter(user => 
          user.role === filterForm.role
        );
      }
      if (filterForm.isActive !== '') {
        filteredUsers = filteredUsers.filter(user => 
          user.isActive === (filterForm.isActive === 'true')
        );
      }
      
      setUsers(filteredUsers);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to apply filters');
    } finally {
      setFilterLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterForm({
      username: '',
      email: '',
      cityId: '',
      role: '',
      isActive: ''
    });
    fetchUsers();
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    setFilterForm({
      ...filterForm,
      [e.target.name]: e.target.value
    });
  };

  const openAddModal = async () => {
    setForm({
      username: '',
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      cityId: '',
      role: 'USER',
      isActive: true
    });
    setFormError('');
    setIsEditMode(false);
    setEditingUserId(null);
    setShowModal(true);
  };

  const openEditModal = async (user: User) => {
    setForm({
      username: user.username,
      email: user.email,
      password: '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phoneNumber: user.phoneNumber || '',
      cityId: user.cityId || '',
      role: user.role || 'USER',
      isActive: user.isActive || false
    });
    setFormError('');
    setIsEditMode(true);
    setEditingUserId(user.id!);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormError('');
  };

  const handleTextFieldInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectInput = (e: SelectChangeEvent<string>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.checked
    });
  };

  const handleSaveUser = async () => {
    if (!form.username || !form.email) {
      setFormError('Username and Email are required');
      return;
    }

    setSaving(true);
    try {
      if (isEditMode && editingUserId) {
        const { password, ...updateDataWithoutPassword } = form;
        // For updates, we can't send password through the regular update API
        // If password is provided, we might need a separate password update endpoint
        await userAPI.updateUser(editingUserId, updateDataWithoutPassword);
        setSuccessMsg('User updated successfully');
      } else {
        if (!form.password) {
          setFormError('Password is required for new users');
          setSaving(false);
          return;
        }
        await userAPI.createUser(form);
        setSuccessMsg('User created successfully');
      }
      await fetchUsers();
      closeModal();
    } catch (e: unknown) {
      const error = e as ApiError;
      setFormError(error.response?.data?.message || error.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    setDeletingId(id);
    try {
      await userAPI.deleteUser(id);
      setSuccessMsg('User deleted successfully');
      await fetchUsers();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to delete user');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      await userAPI.updateUser(user.id!, { isActive: !user.isActive });
      setSuccessMsg(`User ${user.isActive ? 'deactivated' : 'activated'} successfully`);
      await fetchUsers();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to update user status');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleColor = (role: string): 'primary' | 'secondary' | 'default' | 'error' => {
    switch (role) {
      case 'ADMIN':
        return 'error';
      case 'MODERATOR':
        return 'secondary';
      default:
        return 'primary';
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Users Management
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchUsers}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={openAddModal}
            sx={{ borderRadius: 2 }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Filter Section */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filter Users
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Username"
                  name="username"
                  value={filterForm.username}
                  onChange={handleFilterChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Email"
                  name="email"
                  value={filterForm.email}
                  onChange={handleFilterChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>City</InputLabel>
                  <Select
                    name="cityId"
                    value={filterForm.cityId}
                    onChange={handleFilterChange}
                    label="City"
                  >
                    <MenuItem value="">All Cities</MenuItem>
                    {cities.map((city) => (
                      <MenuItem key={city.id} value={city.id}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={filterForm.role}
                    onChange={handleFilterChange}
                    label="Role"
                  >
                    <MenuItem value="">All Roles</MenuItem>
                    {roles.map((role) => (
                      <MenuItem key={role.value} value={role.value}>
                        {role.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Active</InputLabel>
                  <Select
                    name="isActive"
                    value={filterForm.isActive}
                    onChange={handleFilterChange}
                    label="Active"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    onClick={applyFilters}
                    disabled={filterLoading}
                    size="small"
                  >
                    {filterLoading ? <CircularProgress size={16} /> : 'Apply'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={clearFilters}
                    size="small"
                    startIcon={<Clear />}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No users found.</TableCell>
                  </TableRow>
                ) : users.map(user => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {user.username}
                        </Typography>
                        {user.firstName && user.lastName && (
                          <Typography variant="caption" color="text.secondary">
                            {user.firstName} {user.lastName}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                          <Email fontSize="small" />
                          {user.email}
                        </Typography>
                        {user.phoneNumber && (
                          <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                            <Phone fontSize="small" />
                            {user.phoneNumber}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {user.cityName || user.cityId || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.role || 'USER'} 
                        color={getRoleColor(user.role || 'USER')}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={user.isActive ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(user.lastLoginAt)}
                    </TableCell>
                    <TableCell>
                      {formatDate(user.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEditModal(user)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={user.isActive ? 'Deactivate' : 'Activate'}>
                          <IconButton
                            size="small"
                            color={user.isActive ? 'warning' : 'success'}
                            onClick={() => handleToggleStatus(user)}
                          >
                            <Switch size="small" checked={user.isActive} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(user.id!)}
                            disabled={deletingId === user.id}
                          >
                            {deletingId === user.id ? <CircularProgress size={16} /> : <Delete />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={showModal} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={form.username}
                onChange={handleTextFieldInput}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleTextFieldInput}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={form.password}
                onChange={handleTextFieldInput}
                required={!isEditMode}
                helperText={isEditMode ? 'Leave blank to keep current password' : 'Required for new users'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={form.firstName}
                onChange={handleTextFieldInput}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={form.lastName}
                onChange={handleTextFieldInput}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleTextFieldInput}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>City</InputLabel>
                <Select
                  name="cityId"
                  value={form.cityId}
                  onChange={handleSelectInput}
                  label="City"
                >
                  <MenuItem value="">Select City</MenuItem>
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={form.role}
                  onChange={handleSelectInput}
                  label="Role"
                >
                  {roles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isActive"
                    checked={form.isActive}
                    onChange={handleCheckbox}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveUser} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={16} /> : (isEditMode ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users; 